import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { parseWorkersFile, type WorkerImportRow } from '../../domain/excel/importWorkers';
import { generateTempPassword } from '../../lib/tempPassword';
import { approveWorkersBulk, type BulkWorkerInput } from '../../api/workers';
import type { ParseResult } from '../../domain/excel/parseResult';

const BATCH_SIZE = 20;

interface RowResult {
  /** Fila real del Excel, para que el reporte descargable diga "Fila N: motivo". */
  rowNumber: number;
  dni: string;
  email: string;
  tempPassword: string;
  ok: boolean;
  error?: string;
}

export default function TrabajadoresImportar() {
  const [preview, setPreview] = useState<ParseResult<WorkerImportRow> | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState<RowResult[] | null>(null);
  /** Errores de previsualización: se conservan aunque el preview se cierre al importar,
      porque el reporte descargable debe decir qué filas NO entraron y por qué (spec §9). */
  const [parseErrors, setParseErrors] = useState<{ rowNumber: number; message: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onFile(file: File) {
    setResults(null);
    setError(null);
    const parsed = parseWorkersFile(await file.arrayBuffer());
    setParseErrors(parsed.errors);
    setPreview(parsed);
  }

  async function confirmImport() {
    if (!preview || preview.rows.length === 0) return;
    setProcessing(true);
    setError(null);
    const out: RowResult[] = [];
    try {
      // Clave temporal por trabajador, generada antes de enviar
      const passwords = new Map<string, string>();
      for (const w of preview.rows) passwords.set(w.dni, generateTempPassword());

      for (let i = 0; i < preview.rows.length; i += BATCH_SIZE) {
        const batch = preview.rows.slice(i, i + BATCH_SIZE);
        setProgress(`Procesando ${Math.min(i + BATCH_SIZE, preview.rows.length)} de ${preview.rows.length}…`);
        const inputs: BulkWorkerInput[] = batch.map((w) => ({
          dni: w.dni,
          first_names: w.firstNames,
          last_names: w.lastNames,
          phone: w.phone,
          email: w.email,
          bank: w.bank || null,
          account_number: w.accountNumber || null,
          cci: w.cci || null,
          temp_password: passwords.get(w.dni) as string,
        }));
        const batchResults = await approveWorkersBulk(inputs);
        for (const w of batch) {
          const r = batchResults.find((x) => x.dni === w.dni);
          out.push({
            rowNumber: w.rowNumber,
            dni: w.dni,
            email: w.email,
            tempPassword: passwords.get(w.dni) as string,
            ok: r?.ok ?? false,
            error: r ? r.error : 'Sin respuesta del servidor',
          });
        }
      }
      setResults(out);
      setPreview(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al importar trabajadores');
      if (out.length > 0) setResults(out); // resultados parciales de lotes ya procesados
    } finally {
      setProcessing(false);
      setProgress('');
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function downloadCredentials() {
    if (!results) return;
    const okRows = results.filter((r) => r.ok);
    // La primera columna es el DOCUMENTO porque es lo que la persona teclea para entrar. El
    // encabezado decía "Correo" y eso era engañoso: nadie entra con un correo en campo, y desde
    // que el correo es opcional esa columna puede venir vacía. Este archivo es el que usa el
    // coordinador para repartir accesos: tiene que decir exactamente qué escribir.
    const aoa: unknown[][] = [
      ['Documento (con esto entra)', 'Clave temporal', 'Correo de contacto'],
      ...okRows.map((r) => [r.dni, r.tempPassword, r.email === '' ? '(sin correo)' : r.email]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Credenciales');
    XLSX.writeFile(wb, 'credenciales-trabajadores.xlsx');
  }

  /**
   * Reporte descargable de la importación (spec §9: "qué filas entraron y cuáles no, y por qué").
   * Junta los errores de previsualización (`Fila N: motivo`) con el resultado por fila que
   * devolvió la Edge Function. Mismo patrón que el importador de PDV (Task 13).
   */
  function downloadDetail() {
    const okRows = (results ?? []).filter((r) => r.ok);
    const failedRows = (results ?? []).filter((r) => !r.ok);
    const lines = [
      `Importación de trabajadores — ${new Date().toLocaleString('es-PE')}`,
      `Creados: ${okRows.length}`,
      `Rechazados por el servidor: ${failedRows.length}`,
      `Rechazados en la validación del archivo: ${parseErrors.length}`,
      '',
      '--- Filas rechazadas al leer el archivo ---',
      ...(parseErrors.length === 0
        ? ['(ninguna)']
        : parseErrors.map((e) => `Fila ${e.rowNumber}: ${e.message}`)),
      '',
      '--- Filas rechazadas por el servidor ---',
      ...(failedRows.length === 0
        ? ['(ninguna)']
        : failedRows.map((r) => `Fila ${r.rowNumber} (DNI ${r.dni}): ${r.error ?? 'error desconocido'}`)),
      '',
      '--- Filas creadas ---',
      ...(okRows.length === 0
        ? ['(ninguna)']
        : okRows.map((r) => `Fila ${r.rowNumber} (DNI ${r.dni}): creado como ${r.email}`)),
    ];
    const url = URL.createObjectURL(
      new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'importacion-trabajadores.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="border rounded p-4 mb-4">
        <h2 className="font-semibold mb-2">Importar trabajadores desde Excel</h2>
        <p className="text-sm text-gray-600 mb-2">
          Columnas: DNI, Nombres, Apellidos, Celular, Correo, Banco, Cuenta, CCI (primera hoja).
          Cada trabajador se crea ya aprobado, con usuario de acceso y clave temporal.
        </p>
        <input
          ref={fileRef} type="file" accept=".xlsx,.xls" className="max-w-full"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
        />
      </div>

      {error && <p className="text-red-600 mb-2">{error}</p>}

      {preview && (
        <div className="border rounded p-4 mb-4 bg-yellow-50">
          <p className="mb-2">{preview.rows.length} filas válidas · {preview.errors.length} filas con error</p>
          {preview.errors.length > 0 && (
            <ul className="text-sm text-red-700 mb-2 list-disc pl-5">
              {preview.errors.map((e) => <li key={e.rowNumber}>Fila {e.rowNumber}: {e.message}</li>)}
            </ul>
          )}
          <div className="overflow-x-auto mb-2">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="p-1">Fila</th><th className="p-1">DNI</th><th className="p-1">Nombres</th>
                  <th className="p-1">Apellidos</th><th className="p-1">Celular</th><th className="p-1">Correo</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((w) => (
                  <tr key={w.rowNumber} className="border-t">
                    <td className="p-1">{w.rowNumber}</td><td className="p-1">{w.dni}</td>
                    <td className="p-1">{w.firstNames}</td><td className="p-1">{w.lastNames}</td>
                    <td className="p-1">{w.phone}</td><td className="p-1">{w.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <button
              className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
              disabled={processing || preview.rows.length === 0}
              onClick={() => void confirmImport()}
            >
              {processing ? progress || 'Importando…' : `Crear ${preview.rows.length} trabajadores`}
            </button>
            <button className="px-3 py-2 bg-gray-200 rounded" disabled={processing} onClick={() => setPreview(null)}>
              Cancelar
            </button>
            {preview.errors.length > 0 && (
              <button className="px-3 py-2 bg-gray-200 rounded" onClick={downloadDetail}>
                Descargar detalle (.txt)
              </button>
            )}
          </div>
        </div>
      )}

      {results && (
        <div className="border rounded p-4 bg-green-50">
          <div className="flex items-center justify-between mb-2">
            <p>
              {results.filter((r) => r.ok).length} creados, {results.filter((r) => !r.ok).length} con error
              {parseErrors.length > 0 && `, ${parseErrors.length} rechazados al leer el archivo`}
            </p>
            <div className="flex gap-2">
              <button className="px-3 py-2 bg-gray-200 rounded" onClick={downloadDetail}>
                Descargar detalle (.txt)
              </button>
              <button
                className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                disabled={results.every((r) => !r.ok)}
                onClick={downloadCredentials}
              >
                Descargar credenciales
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="p-1">Fila</th><th className="p-1">DNI</th><th className="p-1">Correo</th>
                  <th className="p-1">Resultado</th><th className="p-1">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.dni} className="border-t">
                    <td className="p-1">{r.rowNumber}</td>
                    <td className="p-1">{r.dni}</td>
                    <td className="p-1">{r.email}</td>
                    <td className="p-1">{r.ok ? 'OK' : 'Error'}</td>
                    <td className="p-1 text-red-700">{r.ok ? '' : r.error ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
